import React, {useRef, useState, useCallback} from 'react';
import styled from 'styled-components';
import {EditorState} from 'draft-js';
import {RichTextEditor} from './RichTextEditor';
import {extractMentionIds} from '../utils/insertMention';
import {mentionDecorator} from '../utils/mentionDecorator';
import {RecipientsBar} from './RecipientsBar';
import {AttachmentList} from './AttachmentList';
import {ConfirmModeSwitch} from './ConfirmModeSwitch';
import {useReplySubmit} from '../hooks/useReplySubmit';
import {useCreateComment} from '../hooks/useCreateComment';
import {useAttachmentUpload} from '../hooks/useAttachmentUpload';

type ComposerMode = 'reply' | 'note';

const Shell = styled.div`
	margin-top: 24px;
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;

const TabList = styled.div`
	display: flex;
	border-bottom: 1px solid #e2e8f0;
`;

const Tab = styled.button<{$active: boolean}>`
	border: 0;
	background: transparent;
	padding: 10px 16px;
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 500;
	cursor: pointer;
	transition: color 0.15s;
	border-bottom: 2px solid ${p => (p.$active ? '#6259ff' : 'transparent')};
	color: ${p => (p.$active ? '#4f47d6' : '#64748b')};
	&:hover {
		color: ${p => (p.$active ? '#4f47d6' : '#334155')};
	}
`;

const RecipientsWrap = styled.div`
	padding: 12px 16px 0;
`;

const Body = styled.div<{$note: boolean}>`
	padding: 16px;
	background: ${p => (p.$note ? '#eeedff' : 'transparent')};
`;

const NoteHint = styled.p`
	margin: 0 0 8px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #4f47d6;
`;

const HiddenInput = styled.input`
	display: none;
`;

const ErrorText = styled.p`
	margin-top: 8px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #dc2626;
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-top: 1px solid #e2e8f0;
	padding: 12px 16px;
	background: #f8fafc;
`;

const FooterLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const FooterRight = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const IconButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	background: #ffffff;
	padding: 6px;
	color: #475569;
	cursor: pointer;
	transition: background-color 0.15s, color 0.15s;
	&:hover {
		background: #f8fafc;
		color: #334155;
	}
`;

const PaperclipIcon = styled.svg`
	height: 16px;
	width: 16px;
`;

const GhostButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	background: #ffffff;
	padding: 6px 12px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #475569;
	cursor: pointer;
	transition: background-color 0.15s;
	&:hover {
		background: #f8fafc;
	}
	&:disabled {
		cursor: not-allowed;
		opacity: 0.4;
	}
`;

const AiIcon = styled.svg`
	height: 14px;
	width: 14px;
	color: #6259ff;
`;

const SendButton = styled.button`
	border: 0;
	border-radius: 6px;
	background: #6259ff;
	padding: 6px 16px;
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	transition: background-color 0.15s;
	&:hover {
		background: #4f47d6;
	}
	&:disabled {
		opacity: 0.4;
	}
`;

interface Props {
	ticketId: string;
	requesterEmail: string;
	onSent: () => void;
	/** Used in tests to inject a pre-populated EditorState without fighting Draft.js + jsdom */
	initialEditorState?: EditorState;
	/** Used in tests to start in a specific mode */
	initialMode?: ComposerMode;
}

export function ReplyComposer({ticketId, requesterEmail, onSent, initialEditorState, initialMode}: Props) {
	const [mode, setMode] = useState<ComposerMode>(initialMode ?? 'reply');
	const [editorState, setEditorState] = useState(() => initialEditorState ?? EditorState.createEmpty(mentionDecorator));
	const [drafts, setDrafts] = useState<Record<ComposerMode, EditorState | null>>({
		reply: null,
		note: null,
	});
	const [to, setTo] = useState<string[]>([requesterEmail]);
	const [cc, setCc] = useState<string[]>([]);
	const [bcc, setBcc] = useState<string[]>([]);
	const [pendingMode, setPendingMode] = useState<ComposerMode | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const replySubmit = useReplySubmit();
	const commentSubmit = useCreateComment();
	const attachUpload = useAttachmentUpload();

	const loading = mode === 'reply' ? replySubmit.loading : commentSubmit.loading;
	const replyError = replySubmit.error;
	const noteError = commentSubmit.error;
	const submitError = mode === 'reply' ? replyError : noteError;
	const error = submitError ?? (mode === 'reply' ? attachUpload.error : null);

	const isEmpty = !editorState.getCurrentContent().hasText();

	const requestModeSwitch = useCallback(
		(target: ComposerMode) => {
			if (target === mode) return;
			if (isEmpty) {
				const restoredDraft = drafts[target];
				if (restoredDraft) {
					setEditorState(restoredDraft);
					setDrafts(prev => ({...prev, [target]: null}));
				}
				setMode(target);
			} else {
				setPendingMode(target);
			}
		},
		[mode, isEmpty, drafts]
	);

	const handleKeepDraft = useCallback(() => {
		if (!pendingMode) return;
		// Stash current editor under outgoing mode, then switch and restore any stashed draft
		setDrafts(prev => {
			const restoredDraft = prev[pendingMode];
			setEditorState(restoredDraft ?? EditorState.createEmpty(mentionDecorator));
			return {...prev, [mode]: editorState, [pendingMode]: null};
		});
		setMode(pendingMode);
		setPendingMode(null);
	}, [pendingMode, mode, editorState]);

	const handleDiscard = useCallback(() => {
		if (!pendingMode) return;
		const restoredDraft = drafts[pendingMode];
		if (restoredDraft) {
			setEditorState(restoredDraft);
			setDrafts(prev => ({...prev, [pendingMode]: null}));
		} else {
			setEditorState(EditorState.createEmpty(mentionDecorator));
		}
		setMode(pendingMode);
		setPendingMode(null);
	}, [pendingMode, drafts]);

	const cancelModeSwitch = useCallback(() => {
		setPendingMode(null);
	}, []);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(e.target.files ?? []);
			files.forEach(file => void attachUpload.upload(ticketId, file));
			if (fileInputRef.current) fileInputRef.current.value = '';
		},
		[attachUpload, ticketId]
	);

	const handleSend = useCallback(async () => {
		if (mode === 'reply' && isEmpty) return;
		if (mode === 'note' && isEmpty) return;
		if (mode === 'reply' && to.length === 0) return;

		if (mode === 'reply') {
			const completedAttachments = attachUpload.attachments
				.filter(a => a.progress === 100)
				.map(({storageKey, filename, contentType, sizeBytes}) => ({
					storageKey,
					filename,
					contentType,
					sizeBytes,
				}));
			await replySubmit.submit({
				ticketId,
				editorState,
				to,
				cc: cc.length > 0 ? cc : undefined,
				bcc: bcc.length > 0 ? bcc : undefined,
				attachments: completedAttachments.length > 0 ? completedAttachments : undefined,
			});
		} else if (mode === 'note') {
			const content = editorState.getCurrentContent();
			const mentionIds = extractMentionIds(content);
			let bodyMd = content.getPlainText('\n');
			content.getBlockMap().forEach(block => {
				if (!block) return;
				block.findEntityRanges(
					char => {
						const entityKey = char.getEntity();
						if (!entityKey) return false;
						return content.getEntity(entityKey).getType() === 'MENTION';
					},
					(start, end) => {
						const entityKey = block.getEntityAt(start);
						if (!entityKey) return;
						const entity = content.getEntity(entityKey);
						const data = entity.getData() as {userId: string; name: string};
						const displayText = block.getText().slice(start, end);
						bodyMd = bodyMd.replace(displayText, `@[user:${data.userId}]`);
					}
				);
			});
			await commentSubmit.submit(ticketId, {bodyMd, mentions: mentionIds});
		}

		setEditorState(EditorState.createEmpty(mentionDecorator));
		setDrafts({reply: null, note: null});
		setTo([requesterEmail]);
		setCc([]);
		setBcc([]);
		attachUpload.clear();
		onSent();
	}, [mode, editorState, ticketId, requesterEmail, to, cc, bcc, replySubmit, commentSubmit, attachUpload, isEmpty, onSent]);

	return (
		<Shell>
			<TabList role="tablist">
				<Tab
					role="tab"
					aria-selected={mode === 'reply'}
					$active={mode === 'reply'}
					onClick={() => requestModeSwitch('reply')}
				>
					Reply
				</Tab>
				<Tab
					role="tab"
					aria-selected={mode === 'note'}
					$active={mode === 'note'}
					onClick={() => requestModeSwitch('note')}
				>
					Internal note
				</Tab>
			</TabList>

			{mode === 'reply' && (
				<RecipientsWrap>
					<RecipientsBar to={to} cc={cc} bcc={bcc} onToChange={setTo} onCcChange={setCc} onBccChange={setBcc} />
				</RecipientsWrap>
			)}

			<Body data-testid="composer-body" $note={mode === 'note'}>
				{mode === 'note' && <NoteHint>Only visible to your team</NoteHint>}

				<RichTextEditor
					editorState={editorState}
					onChange={setEditorState}
					placeholder={mode === 'reply' ? 'Write a reply to the customer..' : 'Write an internal note...'}
					mentionEnabled={mode === 'note'}
				/>

				{mode === 'reply' && (
					<>
						<AttachmentList attachments={attachUpload.attachments} onRemove={attachUpload.remove} />
						<HiddenInput
							ref={fileInputRef}
							type="file"
							multiple
							onChange={handleFileChange}
							aria-label="Attach files"
						/>
					</>
				)}

				{error && <ErrorText role="alert">{error}</ErrorText>}
			</Body>

			<Footer>
				<FooterLeft>
					{mode === 'reply' && (
						<IconButton
							type="button"
							aria-label="Attach files"
							title="Attach files"
							onClick={() => fileInputRef.current?.click()}
						>
							<PaperclipIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13"
								/>
							</PaperclipIcon>
						</IconButton>
					)}
					{/* TODO: AI-assisted drafting (future feature) — intentionally inert for now. */}
					<GhostButton type="button" disabled>
						<AiIcon fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
							/>
						</AiIcon>
						Draft with AI
					</GhostButton>
				</FooterLeft>
				<FooterRight>
					<GhostButton type="button">Save draft</GhostButton>
					<SendButton
						type="button"
						aria-label="Send"
						disabled={isEmpty || loading || (mode === 'reply' && to.length === 0)}
						onClick={handleSend}
					>
						{loading ? 'Sending...' : mode === 'reply' ? 'Send reply' : 'Add note'}
					</SendButton>
				</FooterRight>
			</Footer>

			{pendingMode && (
				<ConfirmModeSwitch
					fromMode={mode}
					toMode={pendingMode}
					onKeepDraft={handleKeepDraft}
					onDiscard={handleDiscard}
					onCancel={cancelModeSwitch}
				/>
			)}
		</Shell>
	);
}
